import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import semver from "semver";

const MIN_VERSION_WITH_LICENSES = "3.0.0-0";
const MIN_VERSION_WITHOUT_ZIVID_ONE = "3.5.0-1";

function Version1xError() {
  return (
    <Alert variant="danger">
      <Alert.Heading>Upgrade not available</Alert.Heading>
      <p>
        Unfortunately, upgrade packages for 1.x systems are no longer available.
        No new releases are expected for the 1.x compatible Pickit systems.
      </p>
      <hr />
      <p className="mb-0">
        Do you still need a 1.x system upgrade to the latest 1.10 version?
        Please contact{" "}
        <a href="mailto:support@pickit3d.com">support@pickit3d.com</a>.
      </p>
    </Alert>
  );
}

function Version2Warning() {
  return (
    <Alert variant="warning">
      <Alert.Heading>Warning for versions &lt; 2.4.0</Alert.Heading>
      <p>
        Please contact{" "}
        <a href="mailto:support@pickit3d.com">support@pickit3d.com</a> for the upgrade.
      </p>
    </Alert>
  );
}

function Version33Redirect(systemInfo) {
  return (
    <Alert variant="info">
      <Alert.Heading>Note for versions ≥ 3.3</Alert.Heading>
      <p>
	  Upgrading to a version ≥ 3.3 is only possible from the latest patch of 3.2.<br/>
	  You will need to follow an upgrade path that passes through each minor version.<br/>
	  For example, if you're starting at 3.0, and you'd like to upgrade to 3.5, the upgrade path would be 3.2 (latest patch), 3.3, 3.4, 3.5.
      </p>
    </Alert>
  );
}

function Version34Redirect(systemInfo) {
  return (
    <Alert variant="info">
      <Alert.Heading>Note for versions ≥ 3.4</Alert.Heading>
      <p>
	  Upgrading to a version ≥ 3.4 is only possible from 3.3.<br/>
	  You will need to follow an upgrade path that passes through each minor version.<br/>
	  For example, if you're starting at 3.0, and you'd like to upgrade to 3.5, the upgrade path would be 3.2 (latest patch), 3.3, 3.4, 3.5.
      </p>
    </Alert>
  );
}

function Version35Redirect(systemInfo) {
  return (
    <Alert variant="info">
      <Alert.Heading>Note for versions ≥ 3.5</Alert.Heading>
      <p>
	  Upgrading to a version ≥ 3.5 is only possible from 3.4.<br/>
	  You will need to follow an upgrade path that passes through each minor version.<br/>
	  For example, if you're starting at 3.0, and you'd like to upgrade to 3.5, the upgrade path would be 3.2 (latest patch), 3.3, 3.4, 3.5.
      </p>
    </Alert>
  );
}

function InvalidProcessorRedirect(systemInfo) {
  return (
    <Alert variant="warning">
      <Alert.Heading>Warning</Alert.Heading>
      <p>
	  The system you entered is not a valid processor name.
      </p>
    </Alert>
  );
}

function OldProcessorRedirect(systemInfo) {
  return (
    <Alert variant="warning">
      <Alert.Heading>Warning</Alert.Heading>
      <p>
	  Your system no longer has access to free upgrades, a feature limited to <strong className="font-weight-bold">15 months</strong> after purchase of the license. Please contact sales@pickit3d.com or your known Pickit Sales Representative to discuss how to obtain an upgrade license.
      </p>
    </Alert>
  );
}

function DownloadLinks(props) {
  return (
    <>
      {semver.gte(props.releaseInfo.version, MIN_VERSION_WITHOUT_ZIVID_ONE) && (
        <>
	      <Alert variant="warning">
	      <p>
	      Do not upgrade to this version if you are using a <a href="https://docs.pickit3d.com/en/latest/hardware/cameras/first_gen_cam.html#pickit-high-definition-hd-cameras">first-generation HD camera</a> like an M-HD or L-HD.<br /><img src="./m-hd.jpg" height="120" alt="m-hd" /><br/>
	      The last release supporting first-generation HD cameras is 3.4.<br/>
              Please contact{" "}
              <a href="mailto:support@pickit3d.com">support@pickit3d.com</a> for more details.
	      </p>
	      </Alert>
        </>
      )}
      <h4>Upgrade package</h4>
      <p>
        To upgrade your system, download the package linked below and follow the
        instructions in our{" "}
        <a href="https://docs.pickit3d.com/en/latest/support/pickit-system-software-upgrades.html">
          online documentation
        </a>
        .
      </p>
      <p>
        <Button href={props.releaseInfo.url}>
          Download Pickit {props.releaseInfo.version} upgrade package
        </Button>
	<br/><br />
	<p>
	  To check the integrity of your downloaded file, use <a href="https://emn178.github.io/online-tools/md5_checksum.html">this tool</a> to verify that its MD5 sum equals {props.releaseInfo.hash}.
	</p>
      </p>

      <br />

      {semver.gte(props.releaseInfo.version, MIN_VERSION_WITH_LICENSES) && (
        <>
          <h4>License file</h4>
          <p>
            Since version 3.0, Pickit comes with a licensing system that
            requires a license file. You can download the license file for
            your system below. If the computer from which you run the Pickit
            web interface has an Internet connection, you can also download
            this file from there after the upgrade.
            <br />
          </p>
          <p>
            <Button
              href={`https://license.pickit3d.com/system/${props.systemInfo.systemName}/license`}
            >
              Download license file for {props.systemInfo.systemName}
            </Button>
          </p>
          <p>
            Learn more about Pickit's licensing system in our{" "}
            <a href="https://docs.pickit3d.com/en/latest/documentation/settings/index.html">
              online documentation
            </a>
            . If you have any questions, contact us at{" "}
            <a href="mailto:support@pickit3d.com">support@pickit3d.com</a>.
          </p>
        </>
      )}
    </>
  );
}

function isInvalidSystem(systemInfo) {
// Processor names should match the following formats: pc-\d{5}, pickit-(.)*, im-laptop-\d+, im-desktop-\d+

  const systemName = systemInfo.systemName;
  const validProcessorPatternPc = /pc-\d{5}/;
  const validProcessorPatternPickit = /pickit-.*/;
  const validProcessorPatternLaptop = /im-laptop-\d+/;
  const validProcessorPatternDesktop = /im-desktop-\d+/;

  if (validProcessorPatternPc.test(systemName) ||
      validProcessorPatternLaptop.test(systemName) ||
      validProcessorPatternPickit.test(systemName) ||
      validProcessorPatternDesktop.test(systemName)) {
      return false;
  } else {
    return true;
  }
}

function isOldSystem(systemInfo) {
/*
   Old processors no longer have access to the bundle.
   They are defined as processors shipped before 30.11.2023, approx 15 months before the 3.5 release.
   The shipping date has been taken from https://docs.google.com/spreadsheets/d/1ZvDS0g36Y5KmrC9fxG1vKJMZkl9nIYbsZmMXclz1JI4/edit?gid=595263960#gid=595263960.
*/
  const systemName = systemInfo.systemName;
  const oldProcessorPattern5 = /pc-5/;
  const oldProcessorPattern6 = /pc-6/;
  const oldProcessorPattern7 = /pc-7/;
  const oldProcessorMinimum6 = "pc-60425";
  const oldProcessorMinimum7 = "pc-70028";

  if ((oldProcessorPattern5.test(systemName) && systemName!=="pc-50815") ||
     (oldProcessorPattern6.test(systemName) && systemName<oldProcessorMinimum6 && systemName!=="pc-60003" && systemName!=="pc-60240" && systemName!=="pc-60282" && systemName!=="pc-60407") ||
     (oldProcessorPattern7.test(systemName) && systemName<oldProcessorMinimum7 && systemName!=="pc-70011")) {
      return true;
  } else {
    return false;
  }
}

function GetElement(systemInfo, releaseInfo) {
  if(isInvalidSystem(systemInfo)) {
      return (
       <>
        <InvalidProcessorRedirect />
       </>
      );
  } else if(isOldSystem(systemInfo)) {
      return (
       <>
        <OldProcessorRedirect />
       </>
      );
  } else if (semver.lt(systemInfo.systemVersion, "2.0.0-0")) {
    return <Version1xError />;
  } else if (semver.lt(systemInfo.systemVersion, "2.4.0-0")) {
    return (
      <>
        <Version2Warning />
      </>
    );
  } else if (semver.gte(releaseInfo.version,"3.3.0") && semver.lt(systemInfo.systemVersion, "3.2.3-3")) {
      const release32Info = {
	  version: "3.2.3",
	  url: "https://client.pickit3d.com/releases/pickit-offline-upgrade-bundle_3.2.3-3.tar.gz",
          hash: "243013fb0f402d185959d97ed74db5d9"
      };

      return (
       <>
        <Version33Redirect />
        <DownloadLinks systemInfo={systemInfo} releaseInfo={release32Info} />
       </>
      );
  } else if (semver.gte(releaseInfo.version,"3.4.0-1") && semver.lt(systemInfo.systemVersion, "3.3.0-0")) {
      const release33Info = {
	  version: "3.3.2",
	  url: "https://client.pickit3d.com/releases/pickit-offline-upgrade-bundle_3.3.2-1.tar.gz",
          hash: "56b4b4195b7b74dffe9adcb404bae055"
      };

      return (
       <>
        <Version34Redirect />
        <DownloadLinks systemInfo={systemInfo} releaseInfo={release33Info} />
       </>
      );
  } else if (semver.gte(releaseInfo.version,"3.5.0-1") && semver.lt(systemInfo.systemVersion, "3.4.0-0")) {
      const release34Info = {
	  version: "3.4.4",
	  url: "https://client.pickit3d.com/releases/pickit-offline-upgrade-bundle_3.4.4-1.tar.gz",
          hash: "9a8e775c74fbc7841fc17bd39bad8976"
      };
      return (
       <>
        <Version35Redirect />
        <DownloadLinks systemInfo={systemInfo} releaseInfo={release34Info} />
       </>
      );
  } else if (semver.lt(systemInfo.systemVersion, MIN_VERSION_WITH_LICENSES)) {
    return (
      <>
        <DownloadLinks systemInfo={systemInfo} releaseInfo={releaseInfo} />
      </>
    );
  } else {
    return (
       <>
       <DownloadLinks systemInfo={systemInfo} releaseInfo={releaseInfo} />
       </>
    );
  }
}

function ReleaseNotesUrl(props) {
  if (props.releaseInfo.version === undefined) {
    return "undefined";
  }
  const major = semver.major(props.releaseInfo.version);
  const minor = semver.minor(props.releaseInfo.version);
  return (
    <a
      href={`https://docs.pickit3d.com/en/${major}.${minor}/documentation/release_notes`}
    >
      {props.releaseInfo.version}
    </a>
  );
}

function Copyright() {
  return (
    <div>
      {"Copyright © "}
      Pickit NV {new Date().getFullYear()}
      {"."}
    </div>
  );
}

function UpgradePortal3x() {
  const [state, setState] = useState({
    systemName: "",
    systemVersion: "",
    submitted: false,
    blocked: false,
  });

  const [releaseInfo, setReleaseInfo] = useState({
    version: undefined,
    url: undefined,
  });

  const handleSubmit = (event) => {
    setState({ ...state, submitted: true });

    state.blocked=false;
    if(isOldSystem(state))
	state.blocked=true;

    const formData = new FormData();
    formData.append('system_name', state.systemName);
    formData.append('system_version', state.systemVersion);
    formData.append('target_version', releaseInfo.version);
    formData.append('blocked', state.blocked);

    console.log(state.systemName + ',' + state.systemVersion + ',' + releaseInfo.version + ',' + state.blocked);

    fetch('./log.cgi', {
      method: 'POST',
      body: formData
    }).then(console.debug)
    .catch(console.error);

    event.preventDefault();
  };

  useEffect(() => {
    const customHeaders = new Headers();
    customHeaders.append('pragma', 'no-cache');
    customHeaders.append('cache-control', 'no-cache');
    fetch("./release/version.json", {
      method: 'GET',
      headers: customHeaders,
    }).then((response) => response.json())
      .then(setReleaseInfo);
  }, []);

  return (
    <>
      <Row>
        <Col lg={8}>
          <p className="lead">
            Here you can download a package to upgrade your
            Pickit system to the latest 3.x version (
            <ReleaseNotesUrl releaseInfo={releaseInfo} />
            ), or to the most recent one supported by your system.
          </p>
          <br />
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          {!state.submitted && (
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formSystemId">
                <Form.Label>System ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="pc-xxxxx"
                  value={state.systemName}
                  onChange={(e) => {
                    setState({ ...state, systemName: `${e.target.value}`.trim().toLowerCase() });
                  }}
                  required
                />
                <Form.Text className="text-muted">
                  You can find the System ID in the Pickit web interface's
                  top bar.
                </Form.Text>
              </Form.Group>

              <Form.Group controlId="formSystemVersion">
                <Form.Label>Current Pickit Version</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="x.y.z"
                  value={state.systemVersion}
                  onChange={(e) => {
                    setState({ ...state, systemVersion: e.target.value });
                  }}
                  required
                />
                <Form.Text className="text-muted">
                  You can find the current version in the Pickit web
                  interface's top bar.
                </Form.Text>
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                disabled={
                  !state.systemName || !semver.valid(state.systemVersion)
                }
              >
                Download upgrade package
              </Button>
            </Form>
          )}
        </Col>
      </Row>

      {state.submitted && (
        <div>
          {GetElement(state, releaseInfo)}
          <br />
          <Button
            variant="outline-secondary"
            type="submit"
            onClick={(e) => {
              setState({ ...state, submitted: false });
            }}
          >
            Back
          </Button>
        </div>
      )}
      <br />
    </>
  );
}

function UpgradePortal4x() {
  const [systemName, setSystemName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    const customHeaders = new Headers();
    customHeaders.append('pragma', 'no-cache');
    customHeaders.append('cache-control', 'no-cache');
    fetch("./release/versions4.json", {
      method: 'GET',
      headers: customHeaders,
    }).then((response) => response.json())
      .then(setBundles)
      .catch(console.error);
  }, []);

  // Log the actual download rather than the ID lookup: the Pickit 4 tab offers
  // several bundles, so the chosen package is only known at click time.
  // The form has no version input, so log.cgi's required system_version
  // gets a placeholder. keepalive keeps the request alive if the browser
  // navigates to the file.
  const logDownload = (targetVersion) => {
    const params = new URLSearchParams();
    params.append('system_name', systemName);
    params.append('system_version', '4.x');
    params.append('target_version', targetVersion);
    params.append('blocked', 'false');

    fetch('./log.cgi', {
      method: 'POST',
      body: params,
      keepalive: true,
    }).then(console.debug)
    .catch(console.error);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
  };

  return (
    <>
      <Row>
        <Col lg={8}>
          <p className="lead">
            Here you can download a package to upgrade Pickit systems running version <strong className="font-weight-bold">4.0 or later</strong> to a newer release.<br />
            Additionally, you can download the license file for your Pickit system.
          </p>
          <br />
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          {!submitted && (
            <Form onSubmit={handleSubmit} style={{ marginTop: "1.5rem" }}>
              <Form.Group controlId="formSystemId4x">
                <Form.Label>System ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="pc-xxxxx"
                  value={systemName}
                  onChange={(e) => setSystemName(e.target.value.trim().toLowerCase())}
                  required
                />
                <Form.Text className="text-muted">
                  You can find the System ID in the Pickit web interface's top bar.
                </Form.Text>
              </Form.Group>
              <Button variant="primary" type="submit" disabled={!systemName}>
                Choose upgrade package
              </Button>
            </Form>
          )}
        </Col>
      </Row>

      {submitted && (
        <div style={{ marginTop: "1.5rem" }}>
          {isInvalidSystem({ systemName }) ? (
            <InvalidProcessorRedirect />
          ) : (
            <>
              <h4>Upgrade packages</h4>
              <p>
                To upgrade your system, download a package below and follow the
                instructions in our{" "}
                <a href="https://docs.pickit3d.com/en/latest/support/pickit-system-software-upgrades.html">
                  online documentation
                </a>
                .
              </p>
              {bundles.map((bundle) => (
                <p key={bundle.version}>
                  <Button
                    href={bundle.url}
                    onClick={() => logDownload(bundle.version)}
                  >
                    Download Pickit {bundle.version} upgrade package
                  </Button>
                  <br /><br />
                  <span>
                    MD5: <code>{bundle.hash}</code>
                  </span>
                </p>
              ))}

              <br />

              <h4>License file</h4>
              <p>
                Pickit comes with a licensing system that requires a license file.
                You can download the license file for your system below. If the
                computer from which you run the Pickit web interface has an Internet
                connection, you can also download this file from there after the upgrade.
              </p>
              <p>
                <Button href={`https://license.pickit3d.com/system/${systemName}/license`}>
                  Download license file for {systemName}
                </Button>
              </p>
            </>
          )}

          <br />
          <Button
            variant="outline-secondary"
            onClick={() => setSubmitted(false)}
          >
            Back
          </Button>
        </div>
      )}
      <br />
    </>
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = location.pathname === "/pickit3" ? "3x" : "4x";

  const handleTabSelect = (key) => {
    navigate(key === "3x" ? "/pickit3" : "/pickit4");
  };

  return (
    <div className="App">
      <Container>
        <div className="mx-auto p-3 py-md-5">
          <header className="d-flex align-items-center pb-3 mb-5 border-bottom">
            <a href="https://www.pickit3d.com">
              <img src="./logo.png" height="60" alt="logo" />
            </a>
          </header>

          <h1>Upgrade your Pickit system</h1>
          <br />

          <Tabs activeKey={activeTab} onSelect={handleTabSelect} id="version-tabs" className="mb-4">
            <Tab eventKey="4x" title="Pickit 4">
              <UpgradePortal4x />
            </Tab>
            <Tab eventKey="3x" title="Pickit 3">
              <UpgradePortal3x />
            </Tab>
          </Tabs>

          <footer className="pt-3 my-3 text-muted border-top">
            <Copyright />
          </footer>
        </div>
      </Container>
    </div>
  );
}

export default App;
